// Re-export generated types for convenience
export type {
  CreateDocumentRequest,
  DocumentDetailResponse,
  DocumentListResponse,
  DocumentResponse,
  DocumentSummaryResponse,
  DocumentUpdate,
  ExtractionStatusResponse,
  ExtractionUploadResponse,
  GenerateAndSendRequest,
  GenerateAndSendResponse,
  GetV2ProviderDocumentsApiArg,
} from '@store/generated/documents';

// Re-export generated communication types
export type {
  ChannelDeliveryInfo,
  CommunicationItem,
  CommunicationListResponse,
  PatientInfo as CommunicationPatientInfo,
  FormStatusInfo,
  GetV2ProviderCommunicationsApiArg,
} from '@store/generated/communications';

// Communication delivery status
export type DeliveryStatus =
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'bounced'
  | 'dropped'
  | 'opened'
  | 'clicked';

// Normalized meta-status groups for UI display and filtering
export type MetaDeliveryStatus = 'pending' | 'delivered' | 'failed';

export const META_STATUS_MAP: Record<DeliveryStatus, MetaDeliveryStatus> = {
  queued: 'pending',
  sent: 'pending',
  delivered: 'delivered',
  opened: 'delivered',
  clicked: 'delivered',
  failed: 'failed',
  bounced: 'failed',
  dropped: 'failed',
};

export const META_STATUS_TO_RAW: Record<MetaDeliveryStatus, DeliveryStatus[]> = {
  pending: ['queued', 'sent'],
  delivered: ['delivered', 'opened', 'clicked'],
  failed: ['failed', 'bounced', 'dropped'],
};

export const META_STATUS_LABELS: Record<MetaDeliveryStatus, string> = {
  pending: 'Pending',
  delivered: 'Delivered',
  failed: 'Failed',
};

// Form lifecycle status
export type FormLifecycleStatus = 'pending' | 'in_progress' | 'completed';

// Sent document channel
export type SentDocumentChannel = 'sms' | 'email';

// Sent document type (no appointment — excluded from sent documents page)
export type SentDocumentType = 'form' | 'document' | 'lab';

// Display row for the sent documents DataGrid (CommunicationItem + id for DataGrid)
export interface SentDocumentGroup {
  /** Row key — dispatch_id or first channel's touchpoint_id */
  id: string;
  dispatch_id: string | null;
  communication_type: string;
  /** Human-readable name (e.g., form title, document name) */
  name: string | null;
  patient: {
    patient_id: string;
    first_name?: string | null;
    last_name?: string | null;
    name?: string | null;
    preferred_name?: string | null;
    middle_name?: string | null;
    dob?: string | null;
    identifier?: string | null;
    telephone?: string | null;
  } | null;
  patient_id: string;
  document_id: string | null;
  form_status: {
    form_session_id: string;
    status: string;
    submitted_at?: string | null;
  } | null;
  source: string;
  created_at: string;
  channels: {
    touchpoint_id: string;
    channel: string;
    status: string;
    sent_at?: string | null;
    delivered_at?: string | null;
    failed_at?: string | null;
    error_message?: string | null;
  }[];
}

// Override or add missing extraction types
export interface ExtractedRule {
  title: string;
  body_md: string;
  facets?: {
    cpt?: string[];
    payer?: string[];
    provider_type?: string[];
    [key: string]: any;
  };
  status?: 'pending' | 'approved' | 'rejected';
  approved_rule_id?: string | null;
}

export interface ExtractionFileResponse {
  document_id: string;
  draft_rule_ids?: string[] | null;
  filename: string;
  is_duplicate: boolean;
  llm_doc_type?: string | null;
  llm_summary?: string | null;
  llm_title?: string | null;
  progress?: string | null;
  rules_extracted?: number | null;
  extracted_rules?: ExtractedRule[] | null;
  status?: 'pending' | 'processing' | 'completed' | 'failed' | null;
  thread_id?: string | null;
}

// Minimal types file - add custom types as needed
export interface Document {
  document_id: string;
  filename: string;
  document_title?: string | null;
  document_type?: string | null;
  content_type: string;
  size_bytes: number;
  created_at: string;
  patient_id?: string | null;
}

// Extraction approval types
export interface RuleApproval {
  documentId: string;
  ruleIndex: number;
  modifications?: {
    title?: string;
    body_md?: string;
    facets?: {
      cpt?: string[];
      provider_type?: string[];
      payer?: string[];
    };
  };
}

export interface ApprovedRule {
  documentId: string;
  ruleIndex: number;
  ruleId: string;
}
